import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/auth/signup', userData);
      setPendingEmail(userData.email);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const sendVerificationCode = async (email) => {
    try {
      const response = await api.post('/auth/send-verification', { email });
      setPendingEmail(email);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to send verification code' 
      };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await api.post('/auth/verify', { email, code });
      setPendingEmail(null);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Invalid verification code' 
      };
    }
  };

  const login = async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      setPendingEmail(credentials.email);
      return { success: true, message: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const verify2FA = async (email, code) => {
    try {
      const response = await api.post('/auth/2fa-verify', { email, code });
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await fetchUser();
      setPendingEmail(null);
      
      return { success: true, message: 'Login successful' };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Invalid verification code' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setPendingEmail(null);
  };

  const updateProfile = async (data) => {
    try {
      const response = await api.put('/auth/profile', data);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || 'Update failed' };
    }
  };

  const changePassword = async (payload) => {
    try {
      const response = await api.put('/auth/change-password', payload);
      return { success: true, message: response.data?.message || 'Password changed' };
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || 'Password change failed' };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    pendingEmail,
    signup,
    sendVerificationCode,
    verifyEmail,
    login,
    verify2FA,
    logout,
    updateProfile,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 